import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Handles cross-sell products displayed inside the cart drawer.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const previousButton = target.closest('[data-upsell-previous]');
    const nextButton = target.closest('[data-upsell-next]');
    const addButton = target.closest('[data-upsell-add]');

    if (previousButton) {
      this.#scrollProducts(-1);
      return;
    }

    if (nextButton) {
      this.#scrollProducts(1);
      return;
    }

    if (!(addButton instanceof HTMLButtonElement)) return;

    const variantId = Number(addButton.dataset.variantId);
    if (!variantId || addButton.disabled) return;

    this.#addProduct(variantId, addButton);
  };

  #scrollProducts(direction) {
    const track = this.querySelector('[data-upsell-track]');
    if (!(track instanceof HTMLElement)) return;

    const firstCard = track.querySelector('.cart-upsell__card');
    const cardWidth =
      firstCard instanceof HTMLElement
        ? firstCard.getBoundingClientRect().width
        : track.clientWidth * 0.8;

    track.scrollBy({
      left: direction * (cardWidth + 10),
      behavior: 'smooth',
    });
  }

  async #addProduct(variantId, button) {
    const text = button.querySelector('[data-upsell-button-text]');
    const loader = button.querySelector('[data-upsell-loader]');

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    if (text instanceof HTMLElement) text.textContent = 'Ajout…';
    if (loader instanceof HTMLElement) loader.hidden = false;

    this.#setMessage('', false);

    try {
      const cartAddResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      const cartAddResult = await cartAddResponse.json();

      if (!cartAddResponse.ok) {
        throw new Error(
          cartAddResult.description ||
            cartAddResult.message ||
            "Ce produit n'a pas pu être ajouté."
        );
      }

      if (text instanceof HTMLElement) text.textContent = 'Ajouté ✓';
      this.#setMessage('Produit ajouté à votre panier.', false);

      await this.#refreshDrawer();
    } catch (error) {
      console.error('[cart-upsell]', error);

      const message =
        error instanceof Error
          ? error.message
          : "Une erreur est survenue pendant l'ajout.";

      this.#setMessage(message, true);

      button.disabled = false;
      button.removeAttribute('aria-busy');

      if (text instanceof HTMLElement) text.textContent = 'Réessayer';
      if (loader instanceof HTMLElement) loader.hidden = true;
    }
  }

  async #refreshDrawer() {
    const root = window.Shopify?.routes?.root || '/';
    const sectionId = this.dataset.sectionId || 'cart-drawer-section';

    const sectionResponse = await fetch(`${root}?sections=${encodeURIComponent(sectionId)}`, {
      headers: { Accept: 'application/json' },
    });

    if (!sectionResponse.ok) {
      throw new Error("Le panier n'a pas pu être actualisé.");
    }

    const sections = await sectionResponse.json();
    const sectionHtml = sections[sectionId];

    if (!sectionHtml) {
      throw new Error('La section du panier est introuvable.');
    }

    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(sectionHtml, 'text/html');

    const newWrapper = parsedDocument.querySelector('#shopify-section-cart-drawer-section');
    const currentWrapper = document.querySelector('#shopify-section-cart-drawer-section');

    if (!(newWrapper instanceof HTMLElement) || !currentWrapper) {
      throw new Error("Le contenu du panier n'a pas pu être remplacé.");
    }

    currentWrapper.replaceWith(newWrapper);

    requestAnimationFrame(() => {
      const newDrawer = document.querySelector('#cart-drawer');

      if (newDrawer && typeof newDrawer.open === 'function') {
        newDrawer.open();
        return;
      }

      newDrawer?.setAttribute('open', '');
    });
  }

  #setMessage(message, isError) {
    const messageElement = this.querySelector('[data-upsell-message]');
    if (!(messageElement instanceof HTMLElement)) return;

    messageElement.textContent = message;
    messageElement.hidden = !message;

    if (isError) {
      messageElement.dataset.error = 'true';
    } else {
      delete messageElement.dataset.error;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
